import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const loadLogoFromDB = async (importadoraId, token) => {
  try {
    const response = await axios.get(`${API}/importadoras/${importadoraId}/logo`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data.base64;
  } catch (error) {
    console.warn('Logo no disponible:', error);
    return null;
  }
};

const LiquidacionPDF = {
  generate: async (liquidacion, token) => {
    const doc = new jsPDF('landscape', 'mm', 'letter');
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 10;
    let yPos = margin;

    const formatDate = (dateStr) => {
      if (!dateStr) return '-';
      const date = new Date(dateStr);
      return date.toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    const formatNumber = (value, decimals = 3) => {
      if (value === null || value === undefined) return '-';
      return Number(value).toFixed(decimals);
    };

    const formatUSD = (value) => {
      if (value === null || value === undefined) return '-';
      return `USD $${Math.round(value).toLocaleString('es-CL')}`;
    };

    // ========== CARGAR LOGO SI EXISTE ==========
    let logoBase64 = null;
    if (liquidacion.importadora_id) {
      try {
        logoBase64 = await loadLogoFromDB(liquidacion.importadora_id, token);
      } catch (error) {
        console.warn('No se pudo cargar el logo:', error);
      }
    }

    // ========== TÍTULO PRINCIPAL ==========
    doc.setFillColor(0, 51, 102);
    doc.rect(margin, yPos, pageWidth - 2 * margin, 10, 'F');
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text(`LIQUIDACION: ${liquidacion.dispatch_number} - ${liquidacion.company_alias || liquidacion.company_name}`, pageWidth / 2, yPos + 7, { align: 'center' });
    doc.setTextColor(0, 0, 0);
    yPos += 14;

    // ========== DOS TARJETAS: IMPORTADORA | TRANSPORTISTA ==========
    const cardWidth = (pageWidth - 2 * margin - 4) / 2;
    const cardLeft = margin;
    const cardRight = margin + cardWidth + 4;
    const cardStartY = yPos;
    const cardHeight = 30;

    // --- Tarjeta IMPORTADORA (izquierda) ---
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.3);
    doc.rect(cardLeft, cardStartY, cardWidth, cardHeight);

    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(120, 120, 120);
    doc.text('IMPORTADORA', cardLeft + 3, cardStartY + 4);
    doc.setTextColor(0, 0, 0);

    // Logo
    const logoX = cardLeft + 3;
    const logoY = cardStartY + 6;
    const logoW = 35;
    const logoH = 15;

    if (logoBase64) {
      try {
        doc.addImage(logoBase64, 'AUTO', logoX, logoY, logoW, logoH);
      } catch (error) {
        console.warn('Error al agregar imagen al PDF:', error);
      }
    }

    const textX = logoBase64 ? logoX + logoW + 3 : cardLeft + 3;
    let ty = cardStartY + 10;
    const lineH = 4;

    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text(liquidacion.importadora_razon_social || '', textX, ty);
    ty += lineH;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.text(`RUT: ${liquidacion.importadora_rut || ''}`, textX, ty);
    ty += lineH;
    doc.text(`${liquidacion.importadora_direccion || ''}, ${liquidacion.importadora_comuna || ''}`, textX, ty);
    ty += lineH;
    doc.text(liquidacion.importadora_giro || '', textX, ty);

    // --- Tarjeta TRANSPORTISTA (derecha) ---
    doc.rect(cardRight, cardStartY, cardWidth, cardHeight);

    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(120, 120, 120);
    doc.text('TRANSPORTISTA', cardRight + 3, cardStartY + 4);
    doc.setTextColor(0, 0, 0);

    ty = cardStartY + 10;
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text(liquidacion.company_alias || liquidacion.company_name || '', cardRight + 3, ty);
    ty += lineH;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.text(`Despacho: ${liquidacion.dispatch_number || ''}`, cardRight + 3, ty);
    ty += lineH;
    doc.text(`Producto: ${liquidacion.producto || ''}`, cardRight + 3, ty);
    ty += lineH;
    doc.text(`Cliente: ${liquidacion.trayecto || ''}`, cardRight + 3, ty);

    yPos = cardStartY + cardHeight + 4;

    // ========== TABLA DE DETALLE ==========
    const tableHeaders = [
      'F. CARGA', 'ORIGEN', 'F. RECEP.', 'DESTINO',
      'N° GUÍA', 'PATENTE', 'TON DESP.', 'TON RECEP.',
      'DIF.', 'DSCTO', 'USD'
    ];

    const tableData = liquidacion.trucks?.map(truck => [
      formatDate(truck.load_date),
      truck.origin || '-',
      formatDate(truck.delivery_date),
      truck.destination || '-',
      truck.guide_number || '-',
      truck.license_plate || '-',
      formatNumber(truck.ton_dispatched),
      formatNumber(truck.ton_received),
      formatNumber(truck.difference),
      truck.discount > 0 ? formatNumber(truck.discount) : '-',
      truck.price_usd?.toLocaleString('es-CL') || '0'
    ]) || [];

    const totalsRow = [
      '', '', '', '', '', 'TOTALES:',
      formatNumber(liquidacion.total_ton_dispatched),
      formatNumber(liquidacion.total_ton_received),
      formatNumber(liquidacion.total_difference),
      liquidacion.total_discount > 0 ? formatNumber(liquidacion.total_discount) : '-',
      liquidacion.total_usd?.toLocaleString('es-CL') || '0'
    ];

    autoTable(doc, {
      startY: yPos,
      head: [tableHeaders],
      body: tableData,
      foot: [totalsRow],
      theme: 'grid',
      styles: {
        fontSize: 7,
        cellPadding: 1.5,
        halign: 'center',
        valign: 'middle',
        lineColor: [180, 180, 180],
        lineWidth: 0.2
      },
      headStyles: {
        fillColor: [240, 240, 240],
        textColor: [80, 80, 80],
        fontStyle: 'bold',
        halign: 'center'
      },
      footStyles: {
        fillColor: [240, 240, 240],
        textColor: [0, 0, 0],
        fontStyle: 'bold'
      },
      columnStyles: {
        4: { halign: 'center' },
        5: { fontStyle: 'bold' },
        6: { halign: 'right' },
        7: { halign: 'right' },
        8: { halign: 'right' },
        9: { halign: 'right' },
        10: { halign: 'right' }
      },
      didParseCell: function(data) {
        // Color rojo para DIF positiva (pérdida)
        if (data.column.index === 8 && data.section === 'body') {
          const val = parseFloat(data.cell.raw);
          if (val > 0) data.cell.styles.textColor = [220, 50, 50];
          else data.cell.styles.textColor = [16, 150, 72];
        }
        if (data.column.index === 8 && data.section === 'foot') {
          const val = parseFloat(data.cell.raw);
          if (val > 0) data.cell.styles.textColor = [220, 50, 50];
          else data.cell.styles.textColor = [16, 150, 72];
        }
        // Color rojo para DSCTO
        if (data.column.index === 9 && data.cell.raw !== '-') {
          data.cell.styles.textColor = [220, 50, 50];
        }
        // Color verde para USD
        if (data.column.index === 10) {
          data.cell.styles.textColor = [16, 150, 72];
        }
      },
      margin: { left: margin, right: margin }
    });

    yPos = doc.lastAutoTable?.finalY + 4 || yPos + 50;

    // ========== DOS TARJETAS: RESUMEN DE PAGO | OBSERVACIONES ==========
    const bottomCardHeight = 28;

    // --- Tarjeta RESUMEN DE PAGO (izquierda) ---
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.3);
    doc.rect(cardLeft, yPos, cardWidth, bottomCardHeight);

    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(120, 120, 120);
    doc.text('RESUMEN DE PAGO', cardLeft + 3, yPos + 4);
    doc.setTextColor(0, 0, 0);

    let py = yPos + 9;
    const valueX = cardLeft + cardWidth - 5;

    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'normal');
    doc.text('Total USD:', cardLeft + 3, py);
    doc.setFont('helvetica', 'bold');
    doc.text(formatUSD(liquidacion.total_usd), valueX, py, { align: 'right' });

    py += 4.5;
    doc.setFont('helvetica', 'normal');
    doc.text('Descuentos:', cardLeft + 3, py);
    doc.setTextColor(220, 50, 50);
    doc.text(`-${formatUSD(liquidacion.descuentos || 0)}`, valueX, py, { align: 'right' });
    doc.setTextColor(0, 0, 0);

    py += 4.5;
    doc.text('Anticipos:', cardLeft + 3, py);
    doc.setTextColor(200, 130, 0);
    doc.text(`-${formatUSD(liquidacion.anticipos || 0)}`, valueX, py, { align: 'right' });
    doc.setTextColor(0, 0, 0);

    py += 2;
    doc.line(cardLeft + 3, py, cardLeft + cardWidth - 3, py);
    py += 4;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.text('A PAGAR:', cardLeft + 3, py);
    doc.setTextColor(30, 100, 200);
    doc.text(formatUSD(liquidacion.a_pagar), valueX, py, { align: 'right' });
    doc.setTextColor(0, 0, 0);

    // --- Tarjeta OBSERVACIONES (derecha) ---
    doc.setDrawColor(200, 200, 200);
    doc.rect(cardRight, yPos, cardWidth, bottomCardHeight);

    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(120, 120, 120);
    doc.text('OBSERVACIONES', cardRight + 3, yPos + 4);
    doc.setTextColor(0, 0, 0);

    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'normal');
    const obsText = liquidacion.observacion || 'SIN OBSERVACIONES';
    doc.text(obsText.substring(0, 100), cardRight + 3, yPos + 9);

    if (liquidacion.numero_factura) {
      doc.setFont('helvetica', 'bold');
      doc.text('N° Factura:', cardRight + 3, yPos + 16);
      doc.setFont('helvetica', 'normal');
      doc.text(liquidacion.numero_factura, cardRight + 25, yPos + 16);
    }
    if (liquidacion.numero_cheque) {
      doc.setFont('helvetica', 'bold');
      doc.text('N° Cheque:', cardRight + 3, yPos + 20);
      doc.setFont('helvetica', 'normal');
      doc.text(liquidacion.numero_cheque, cardRight + 25, yPos + 20);
    }

    // ========== PIE DE PÁGINA ==========
    doc.setFontSize(6);
    doc.setTextColor(150, 150, 150);
    doc.text(
      `Generado el ${new Date().toLocaleDateString('es-CL')} - CIG`,
      pageWidth / 2,
      doc.internal.pageSize.getHeight() - 5,
      { align: 'center' }
    );

    // Guardar con nombre automatizado
    // Formato: LIQ_[DESPACHO]_[EMPRESA]_[FECHA].pdf
    const sanitize = (str) => (str || '').replace(/[^a-zA-Z0-9]/g, '_').replace(/_+/g, '_');
    const dateStr = new Date().toISOString().split('T')[0].replace(/-/g, '');
    const fileName = `LIQ_${sanitize(liquidacion.dispatch_number)}_${sanitize(liquidacion.company_alias || liquidacion.company_name)}_${dateStr}.pdf`;
    doc.save(fileName);
    return fileName;
  }
};

export default LiquidacionPDF;
